import React, { useState, useEffect } from "react";
import Layout from "../components/ui/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle, Star } from "lucide-react";

// ─── Form submission via Formspree ───────────────────────────────────────────
// Sign up free at https://formspree.io, create a form, and replace
// the placeholder below with your form endpoint:
const FORMSPREE_ENDPOINT = "https://formspree.io/f/YOUR_FORM_ID";

// ─── Data ─────────────────────────────────────────────────────────────────────
const categories = [
  "Overall impression",
  "Case studies",
  "Visual design",
  "UX writing",
  "Accessibility",
  "Something else",
];

const ratingLabels = {
  1: "Needs a lot of work",
  2: "Could be better",
  3: "It's decent",
  4: "Really good",
  5: "Love it",
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
      {children}
    </p>
  );
}

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;

  return (
    <div>
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`Rate ${n} out of 5`}
            className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black rounded"
          >
            <Star
              size={28}
              className={`transition-colors ${
                n <= display
                  ? "fill-white text-white"
                  : "fill-transparent text-gray-600"
              }`}
            />
          </button>
        ))}
        <AnimatePresence mode="wait">
          {display > 0 && (
            <motion.span
              key={display}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 6 }}
              className="text-sm text-gray-400 ml-1"
            >
              {ratingLabels[display]}
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const Feedback = () => {
  const [form, setForm] = useState({
    name: "",
    role: "",
    category: "",
    rating: 0,
    message: "",
    anonymous: false,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.rating) e.rating = "Please select a rating.";
    if (!form.category) e.category = "Please pick a category.";
    if (!form.message.trim()) e.message = "Please share your thoughts.";
    return e;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) {
      setErrors(e2);
      return;
    }

    setStatus("submitting");

    // If you haven't set up Formspree yet, we show success after a short delay
    // so the UI is still demonstrable. Replace FORMSPREE_ENDPOINT to go live.
    if (FORMSPREE_ENDPOINT.includes("YOUR_FORM_ID")) {
      setTimeout(() => setStatus("success"), 900);
      return;
    }

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.anonymous ? "Anonymous" : form.name || "Anonymous",
          role: form.role,
          category: form.category,
          rating: `${form.rating}/5. ${ratingLabels[form.rating]}`,
          message: form.message,
        }),
      });
      if (res.ok) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  // ── Success state ───────────────────────────────────────────────────────────
  if (status === "success") {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-md"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              className="mb-8 flex justify-center"
            >
              <CheckCircle size={56} className="text-white" strokeWidth={1.5} />
            </motion.div>
            <h2 className="text-3xl font-light text-white mb-4">
              Thank you.
            </h2>
            <p className="text-gray-400 leading-relaxed mb-10">
              Your feedback means a lot. I read every single one and use it to
              grow, as a designer and as a person.
            </p>
            <button
              onClick={() => {
                setStatus("idle");
                setForm({ name: "", role: "", category: "", rating: 0, message: "", anonymous: false });
                setErrors({});
              }}
              className="text-sm text-gray-500 hover:text-white transition-colors underline underline-offset-4"
            >
              Leave another note
            </button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <div className="border-b border-gray-800 py-16 px-6 sm:px-8 md:px-16">
          <div className="max-w-2xl mx-auto">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.p variants={fadeUp} className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Feedback
              </motion.p>
              <motion.h1 variants={fadeUp} className="text-4xl font-light text-white mb-5">
                What do you think?
              </motion.h1>
              <motion.p variants={fadeUp} className="text-gray-400 leading-relaxed">
                Honest feedback, good, bad, or brutally honest, helps me improve.
                No account needed. Takes about 2 minutes.
              </motion.p>
            </motion.div>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto px-6 sm:px-8 md:px-16 py-16">
          <motion.form
            onSubmit={handleSubmit}
            initial="hidden"
            animate="visible"
            variants={stagger}
            noValidate
          >
            {/* ── Rating ───────────────────────────────────────────────── */}
            <motion.div variants={fadeUp} className="mb-10">
              <SectionLabel>Overall Rating *</SectionLabel>
              <StarRating
                value={form.rating}
                onChange={(v) => handleChange("rating", v)}
              />
              {errors.rating && (
                <p className="text-xs text-red-400 mt-2">{errors.rating}</p>
              )}
            </motion.div>

            {/* ── Category ─────────────────────────────────────────────── */}
            <motion.div variants={fadeUp} className="mb-10">
              <SectionLabel>What's your feedback about? *</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => handleChange("category", cat)}
                    className={`text-xs px-3 py-1.5 border transition-colors ${
                      form.category === cat
                        ? "border-white text-white bg-white/10"
                        : "border-gray-700 text-gray-400 hover:border-gray-400 hover:text-white"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {errors.category && (
                <p className="text-xs text-red-400 mt-2">{errors.category}</p>
              )}
            </motion.div>

            {/* ── Message ──────────────────────────────────────────────── */}
            <motion.div variants={fadeUp} className="mb-10">
              <SectionLabel>Your thoughts *</SectionLabel>
              <textarea
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                placeholder="Be as specific as you like, what worked, what didn't, what surprised you..."
                rows={5}
                className={`w-full bg-transparent border text-white placeholder-gray-600 rounded px-4 py-3 text-sm focus:outline-none transition-colors resize-none ${
                  errors.message ? "border-red-500" : "border-gray-700 focus:border-white"
                }`}
              />
              <div className="flex justify-between mt-1">
                {errors.message ? (
                  <p className="text-xs text-red-400">{errors.message}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-gray-600 ml-auto">{form.message.length} / 500</p>
              </div>
            </motion.div>

            {/* ── Name & Role ───────────────────────────────────────────── */}
            <motion.div variants={fadeUp} className="mb-10">
              <SectionLabel>About you (optional)</SectionLabel>
              <div className={`space-y-3 ${form.anonymous ? "opacity-40 pointer-events-none" : ""}`}>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Your name"
                  maxLength={80}
                  className="w-full bg-transparent border border-gray-700 text-white placeholder-gray-600 rounded px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors"
                />
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                  placeholder="Your role (e.g. UX Designer, Hiring Manager, Developer)"
                  maxLength={100}
                  className="w-full bg-transparent border border-gray-700 text-white placeholder-gray-600 rounded px-4 py-3 text-sm focus:outline-none focus:border-white transition-colors"
                />
              </div>
              <label className="flex items-center gap-2 mt-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.anonymous}
                  onChange={(e) => handleChange("anonymous", e.target.checked)}
                  className="w-4 h-4 rounded border border-gray-600 accent-white"
                />
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                  Submit anonymously
                </span>
              </label>
            </motion.div>

            {/* ── Submit ────────────────────────────────────────────────── */}
            <motion.div variants={fadeUp} className="border-t border-gray-800 pt-8">
              {status === "error" && (
                <p className="text-sm text-red-400 mb-4">
                  Something went wrong. Please try again or email me directly at{" "}
                  <a href="mailto:jiadiyounghe@gmail.com" className="underline">
                    jiadiyounghe@gmail.com
                  </a>
                  .
                </p>
              )}
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-gray-600">
                  * Required fields
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="group flex items-center gap-2 bg-white text-black px-6 py-3 text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? (
                    <>
                      <span>Sending</span>
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block w-4 h-4 border-2 border-black border-t-transparent rounded-full"
                      />
                    </>
                  ) : (
                    <>
                      <span>Send Feedback</span>
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.form>
        </div>
      </div>
    </Layout>
  );
};

export default Feedback;
